import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, ArrowRight, TrendUp, Lightning, Eye, Clock, ArrowsClockwise, X } from "@phosphor-icons/react";

const PRICE = 99;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
};

export default function CandidateFeatured() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/me/featured/status")
        .then((r) => setState(r.data))
        .catch(() => setState({ active: false, price_inr: PRICE })), []);
    useEffect(() => { load(); }, [load]);

    const buy = async () => {
        setBusy(true);
        try {
            const rzOk = await new Promise((resolve) => {
                if (window.Razorpay) return resolve(true);
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = () => resolve(true);
                s.onerror = () => resolve(false);
                document.body.appendChild(s);
            });
            if (!rzOk) throw new Error("Couldn't load payment gateway");
            const { data: order } = await api.post("/me/featured/checkout");
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: `Featured Candidate · ${order.duration_days} days`,
                theme: { color: "#F59E0B" },
                handler: async (rp) => {
                    try {
                        const v = await api.post("/me/featured/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        toast.success(`You're Featured until ${fmtDate(v.data.featured_until)}!`);
                        load();
                    } catch { toast.error("Payment verification failed"); }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally { setBusy(false); }
    };

    const subscribeAutoRenew = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/me/featured/auto-renew/subscribe");
            if (data.short_url) {
                toast.success("Complete the mandate on Razorpay to activate auto-renew");
                window.open(data.short_url, "_blank", "noopener,noreferrer");
            } else {
                toast.success("Auto-renew subscribed");
            }
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : (d?.message || "Couldn't start auto-renew"));
        } finally { setBusy(false); }
    };

    const cancelAutoRenew = async () => {
        if (!window.confirm("Stop auto-renew? Your current 30-day Featured window stays active — you just won't be charged again automatically.")) return;
        setBusy(true);
        try {
            await api.post("/me/featured/auto-renew/cancel");
            toast.success("Auto-renew cancelled");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't cancel");
        } finally { setBusy(false); }
    };

    if (!state) return <div className="p-10 text-[#525252]">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="featured-page">
            <div className="mb-6">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-600 mb-2 inline-flex items-center gap-1.5">
                    <Star size={11} weight="fill" /> Top placement · Recruiter attention
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
                    Stand out with the <span className="text-amber-500">gold star</span>.
                </h1>
                <p className="text-sm sm:text-base text-[#525252] mt-3 max-w-2xl">
                    Featured candidates appear at the <b>very top</b> of every recruiter search in
                    the Talent Marketplace across all agencies. Just ₹{PRICE} for 30 days.
                </p>
            </div>

            {/* Active state */}
            {state.active && (
                <div data-testid="featured-active-banner"
                     className="mb-6 bg-amber-50 border-2 border-amber-500 p-6">
                    <div className="flex items-center gap-3">
                        <Star size={36} weight="fill" className="text-amber-500" />
                        <div className="flex-1">
                            <h2 className="font-heading text-2xl font-black text-amber-700">You&apos;re Featured.</h2>
                            <p className="text-xs text-amber-800/80 mt-0.5 flex items-center gap-3 flex-wrap">
                                <span className="inline-flex items-center gap-1">
                                    <Clock size={12} weight="fill" /> {state.days_remaining} day{state.days_remaining === 1 ? "" : "s"} remaining
                                </span>
                                <span>· until {fmtDate(state.featured_until)}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={buy} disabled={busy} data-testid="featured-extend-btn"
                            className="mt-4 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1">
                        Extend by 30 days <ArrowRight size={12} weight="bold" />
                    </button>
                </div>
            )}

            {/* Benefits */}
            <div className="bg-white border border-[#E5E5E5] p-6 sm:p-8 mb-6">
                <h2 className="font-heading text-xl font-black text-ink mb-4">Why Featured pays for itself</h2>
                <ul className="space-y-3 mb-6">
                    {[
                        { icon: TrendUp, t: "3x more recruiter views · guaranteed top-of-search placement" },
                        { icon: Star, t: "Gold star + \"Featured\" pill on every card, in every workspace" },
                        { icon: Eye, t: "Recruiters see you first when they filter for Featured candidates" },
                        { icon: Lightning, t: "30 days coverage — cheaper than a single interview lost" },
                    ].map(({ icon: I, t }, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#404040]">
                            <I size={18} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                            {t}
                        </li>
                    ))}
                </ul>
                <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#F5F5F5]">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">30 days</p>
                        <p className="font-heading text-3xl font-black text-ink">₹{PRICE}</p>
                    </div>
                    <button onClick={buy} disabled={busy} data-testid="featured-buy-btn"
                            className="px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60">
                        {busy ? "Loading…" : (<>{state.active ? "Buy 30 more days" : "Go featured"} <ArrowRight size={14} weight="bold" /></>)}
                    </button>
                </div>
                <p className="text-[10px] text-[#A3A3A3] mt-4">
                    One-time charge — no auto-renewal. Featured status extends from your current expiry date, so early renewals don&apos;t lose days.
                </p>
            </div>

            {/* Auto-renew card */}
            {state.auto_renew?.configured && (
                <div className="bg-white border border-[#E5E5E5] p-6 sm:p-8" data-testid="featured-autorenew-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 mb-3">
                                <ArrowsClockwise size={11} weight="bold" /> Auto-renew · ₹99/month
                            </div>
                            <h3 className="font-heading text-xl font-black text-ink mb-1">Never lose your gold star</h3>
                            <p className="text-sm text-[#525252] max-w-lg">
                                Set it once — Razorpay auto-charges ₹99 every 30 days and extends your Featured window.
                                Cancel any time; your current window stays active till it naturally ends.
                            </p>
                        </div>
                        {state.auto_renew.active ? (
                            <button onClick={cancelAutoRenew} disabled={busy}
                                    data-testid="featured-autorenew-cancel-btn"
                                    className="text-[10px] font-black uppercase tracking-wider px-4 py-2 border-2 border-red-500 text-red-500 hover:bg-red-50 disabled:opacity-60 inline-flex items-center gap-1">
                                <X size={12} weight="bold" /> Cancel auto-renew
                            </button>
                        ) : (
                            <button onClick={subscribeAutoRenew} disabled={busy}
                                    data-testid="featured-autorenew-subscribe-btn"
                                    className="text-[10px] font-black uppercase tracking-wider px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60 inline-flex items-center gap-1">
                                <ArrowsClockwise size={12} weight="bold" /> Turn on auto-renew
                            </button>
                        )}
                    </div>
                    {state.auto_renew.active && (
                        <div className="mt-4 pt-4 border-t border-[#F5F5F5] text-[11px] text-[#525252] flex flex-wrap gap-3" data-testid="featured-autorenew-status">
                            <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                Status: <b className="text-ink">{state.auto_renew.status || "active"}</b>
                            </span>
                            <span className="text-[#E5E5E5]">·</span>
                            <span>Subscription {state.auto_renew.subscription_id?.slice(-8)}</span>
                            {state.auto_renew.short_url && (
                                <>
                                    <span className="text-[#E5E5E5]">·</span>
                                    <a href={state.auto_renew.short_url} target="_blank" rel="noopener noreferrer"
                                       data-testid="featured-autorenew-mandate-link"
                                       className="text-cobalt underline hover:text-ink">Manage mandate ↗</a>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
